/*
 * Copyright (c) CIRROM 2024.
 */

import { Action } from "../../api/ruleengine";
import React, { useEffect, useState } from "react";
import { toReadableText } from "../utils/utils";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { ListNameAutocomplete } from "../utils/datasources/AutoComplete";

export function RuleAction(
  props: Readonly<{
    onAdd?(action: Action): void;
    onRemove?(action: Action): void;
    onActionChange(action: Action): void;
    availableActions: Action[];
    selectedAction: string;
    disableRuleEdits: boolean;
    selectedActionValue: string;
    integratedSystemId?: number;
  }>
) {
  const {
    availableActions,
    onRemove,
    onAdd,
    onActionChange,
    disableRuleEdits,
  } = props;

  const [selectedAction, setSelectedAction] = useState<string>(
    props.selectedAction
  );

  const [userInput, setUserInput] = useState<string>(props.selectedActionValue);

  useEffect(() => {
    onActionChange({ action_type: selectedAction, action_value: userInput });
  }, [selectedAction, userInput]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "0.5em",
      }}
      data-testid={"rule-action"}
    >
      <Select
        variant={"standard"}
        data-testid={"select"}
        value={selectedAction || ""}
        onChange={(event) => {
          setSelectedAction(event.target.value);
        }}
        style={{ flex: 1, minWidth: "200px" }}
        disabled={disableRuleEdits}
      >
        {availableActions.map((availableAction) => {
          return (
            <MenuItem
              key={availableAction.action_type}
              value={availableAction.action_type}
            >
              {toReadableText(availableAction.action_type)}
            </MenuItem>
          );
        })}
      </Select>
      {["ADD_TO_LIST", "REMOVE_FROM_LIST"].includes(selectedAction) &&
        !!props.integratedSystemId && (
          <ListNameAutocomplete
            integratedSystemId={props.integratedSystemId}
            onSelectionChanged={(val) => setUserInput(val)}
            selectedValue={userInput}
          />
        )}
      {selectedAction &&
        availableActions.filter(
          (action) => action.action_type === selectedAction
        ).length > 0 &&
        availableActions.filter(
          (action) => action.action_type === selectedAction
        )[0].requires_input && (
          <TextField
            variant={"standard"}
            margin={"dense"}
            size={"small"}
            disabled={disableRuleEdits}
            onChange={(event) => {
              let val: any = event.target.value;

              setUserInput(val);
            }}
            value={userInput}
            style={{ flex: 1, margin: 0 }}
          />
        )}
      <div style={{ display: "flex" }}>
        <IconButton
          data-testid={"remove_action_btn"}
          aria-label="remove_action"
          color="primary"
          disabled={!onRemove || disableRuleEdits}
          onClick={(_event) =>
            onRemove({ action_type: selectedAction, action_value: "" })
          }
          size="small"
        >
          <RemoveCircleOutlineIcon />
        </IconButton>
        <IconButton
          data-testid={"add_action_btn"}
          aria-label="add_action"
          color="primary"
          size="small"
          disabled={!onAdd || disableRuleEdits}
          onClick={(_event) =>
            onAdd({ action_type: selectedAction, action_value: "" })
          }
        >
          <AddCircleOutlineOutlinedIcon />
        </IconButton>
      </div>
    </div>
  );
}
